import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { MessageSquare, Save, RotateCcw, Eye, Pencil, Check, Info, Wifi, WifiOff, QrCode, RefreshCw, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";

export default function WaTemplatesPage() {
  const [data, setData] = useState({ templates: [], placeholders: [], policy_types: [] });
  const [loading, setLoading] = useState(true);
  const [selectedNotifType, setSelectedNotifType] = useState("pre_expiry");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editText, setEditText] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [saving, setSaving] = useState(false);

  // ── Stato connessione WhatsApp ──
  const [waStatus, setWaStatus] = useState("loading"); // loading|connected|qr_ready|disconnected|reconnecting|logged_out|unreachable
  const [waQrDataUrl, setWaQrDataUrl] = useState(null);
  const [waPolling, setWaPolling] = useState(false);
  const [waLoggingOut, setWaLoggingOut] = useState(false);
  const pollingRef = useRef(null);

  const fetchWaStatus = useCallback(async () => {
    try {
      const res = await axios.get("/api/wa-bridge/wa-status");
      if (res.data.ok) {
        setWaStatus(res.data.status);
        setWaQrDataUrl(res.data.qr_data_url || null);
      } else {
        setWaStatus(res.data.status || "unreachable");
        setWaQrDataUrl(null);
      }
    } catch {
      setWaStatus("unreachable");
      setWaQrDataUrl(null);
    }
  }, []);

  // Polling: quando non è connesso, controlla ogni 3s per mostrare il QR aggiornato
  useEffect(() => {
    fetchWaStatus();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [fetchWaStatus]);

  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (waStatus !== "connected" && waStatus !== "unreachable") {
      pollingRef.current = setInterval(fetchWaStatus, 3000);
    } else {
      // Se connesso, polling lento (ogni 30s) per rilevare disconnessioni
      pollingRef.current = setInterval(fetchWaStatus, 30000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [waStatus, fetchWaStatus]);

  const handleWaLogout = async () => {
    setWaLoggingOut(true);
    try {
      await axios.post("/api/wa-bridge/wa-logout");
      toast.success("Disconnessione avviata, attendi il nuovo QR...");
      setWaStatus("disconnected");
      setWaQrDataUrl(null);
    } catch {
      toast.error("Errore disconnessione");
    } finally {
      setWaLoggingOut(false);
    }
  };

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get("/api/wa-templates/");
      setData(res.data);
    } catch {
      toast.error("Errore caricamento template");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const filteredTemplates = data.templates.filter(t => t.notif_type === selectedNotifType);

  const handleEdit = (tmpl) => {
    setEditingTemplate(tmpl);
    setEditText(tmpl.template);
  };

  const handleSave = async () => {
    if (!editingTemplate || !editText.trim()) return;
    setSaving(true);
    try {
      await axios.put(
        `/api/wa-templates/${editingTemplate.notif_type}/${editingTemplate.policy_type}`,
        { template: editText }
      );
      toast.success("Template salvato");
      setEditingTemplate(null);
      fetchTemplates();
    } catch {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (tmpl) => {
    try {
      const res = await axios.delete(`/api/wa-templates/${tmpl.notif_type}/${tmpl.policy_type}`);
      toast.success("Template ripristinato");
      fetchTemplates();
    } catch {
      toast.error("Errore ripristino");
    }
  };

  const handlePreview = async (template) => {
    try {
      const res = await axios.post("/api/wa-templates/preview", {
        template: template,
        policy_type: editingTemplate?.policy_type || "auto"
      });
      setPreviewText(res.data.preview);
      setPreviewOpen(true);
    } catch {
      toast.error("Errore anteprima");
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="h-40 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto" data-testid="wa-templates-page">
      <PageHeader
        titolo="Template WhatsApp"
        descrizione="Personalizza i messaggi di promemoria scadenze inviati ai clienti su WhatsApp"
        icona={<MessageSquare className="h-7 w-7 text-green-600" />}
        testIdTitolo="wa-templates-title"
      />

      {/* ── Pannello Connessione WhatsApp ────────────────── */}
      <Card data-testid="wa-connection-panel" className={
        waStatus === "connected" ? "border-green-300 bg-green-50/40" :
        waStatus === "qr_ready" ? "border-amber-300 bg-amber-50/40" :
        waStatus === "unreachable" ? "border-red-300 bg-red-50/40" :
        "border-muted"
      }>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              {waStatus === "connected" ? (
                <Wifi className="h-4 w-4 text-green-600" />
              ) : waStatus === "qr_ready" ? (
                <QrCode className="h-4 w-4 text-amber-600" />
              ) : waStatus === "reconnecting" || waStatus === "loading" ? (
                <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
              ) : (
                <WifiOff className="h-4 w-4 text-red-500" />
              )}
              Connessione WhatsApp
            </div>
            <div className="flex items-center gap-2">
              <Badge
                data-testid="wa-status-badge"
                variant={waStatus === "connected" ? "default" : "secondary"}
                className={
                  waStatus === "connected" ? "bg-green-600 text-white" :
                  waStatus === "qr_ready" ? "bg-amber-500 text-white" :
                  waStatus === "unreachable" ? "bg-red-500 text-white" :
                  waStatus === "reconnecting" ? "bg-blue-500 text-white" :
                  ""
                }
              >
                {waStatus === "connected" ? "Connesso" :
                 waStatus === "qr_ready" ? "Scansiona QR" :
                 waStatus === "reconnecting" ? "Riconnessione..." :
                 waStatus === "logged_out" ? "Disconnesso" :
                 waStatus === "unreachable" ? "Bridge offline" :
                 waStatus === "loading" ? "Caricamento..." :
                 "Disconnesso"}
              </Badge>
              {waStatus === "connected" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleWaLogout}
                  disabled={waLoggingOut}
                  className="h-7 text-xs text-muted-foreground hover:text-red-600"
                  data-testid="wa-logout-btn"
                >
                  {waLoggingOut ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />}
                  <span className="ml-1">Disconnetti</span>
                </Button>
              )}
              {(waStatus === "logged_out" || waStatus === "disconnected") && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleWaLogout}
                  disabled={waLoggingOut}
                  className="h-7 text-xs"
                  data-testid="wa-reconnect-btn"
                >
                  {waLoggingOut ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  <span className="ml-1">Genera QR</span>
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        {(waStatus === "qr_ready" && waQrDataUrl) && (
          <CardContent className="pt-0">
            <div className="flex flex-col items-center gap-3 py-2">
              <p className="text-sm text-muted-foreground text-center">
                Apri WhatsApp sul telefono &rarr; <strong>Dispositivi collegati</strong> &rarr; <strong>Collega dispositivo</strong>
              </p>
              <div className="bg-white p-3 rounded-xl shadow-sm border" data-testid="wa-qr-code">
                <img src={waQrDataUrl} alt="QR Code WhatsApp" className="w-[250px] h-[250px]" />
              </div>
              <p className="text-xs text-muted-foreground">Il QR si aggiorna automaticamente</p>
            </div>
          </CardContent>
        )}
        {waStatus === "unreachable" && (
          <CardContent className="pt-0">
            <p className="text-sm text-red-600">
              Il servizio WA-Bridge non e' raggiungibile. Verifica che il container sia attivo.
            </p>
          </CardContent>
        )}
      </Card>

      {/* Placeholders info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Info className="h-4 w-4" />
            Variabili disponibili
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {data.placeholders.map(p => (
              <Badge
                key={p.key}
                variant="outline"
                className="cursor-help text-xs font-mono"
                title={`${p.description} — es: ${p.example}`}
                data-testid={`placeholder-${p.key}`}
              >
                {p.key} <span className="text-muted-foreground ml-1 font-normal">{p.description}</span>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Type selector */}
      <div className="flex items-center gap-3">
        <Label className="text-sm font-medium">Tipo notifica:</Label>
        <Select value={selectedNotifType} onValueChange={setSelectedNotifType}>
          <SelectTrigger className="w-[250px]" data-testid="notif-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pre_expiry">Prima della scadenza (15 giorni)</SelectItem>
            <SelectItem value="post_expiry">Dopo la scadenza (12 giorni)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Templates grid */}
      <div className="grid gap-3">
        {filteredTemplates.map(tmpl => (
          <Card
            key={`${tmpl.notif_type}_${tmpl.policy_type}`}
            className={tmpl.is_custom ? "border-green-200 bg-green-50/30" : ""}
            data-testid={`template-card-${tmpl.policy_type}`}
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-sm">{tmpl.policy_type_label}</span>
                    {tmpl.is_custom && (
                      <Badge variant="default" className="text-[10px] bg-green-600">Personalizzato</Badge>
                    )}
                    {!tmpl.is_custom && (
                      <Badge variant="secondary" className="text-[10px]">Default</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed break-words">
                    {tmpl.template}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(tmpl)}
                    data-testid={`edit-template-${tmpl.policy_type}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {tmpl.is_custom && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReset(tmpl)}
                      title="Ripristina default"
                      data-testid={`reset-template-${tmpl.policy_type}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={(open) => { if (!open) setEditingTemplate(null); }}>
        <DialogContent className="max-w-xl" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              Modifica Template — {editingTemplate?.policy_type_label}
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({editingTemplate?.notif_type_label})
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Messaggio</Label>
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={6}
                className="text-sm"
                placeholder="Scrivi il template del messaggio..."
                data-testid="template-edit-textarea"
              />
              <p className="text-xs text-muted-foreground">
                Usa le variabili: {"{nome}"}, {"{tipo}"}, {"{compagnia}"}, {"{numero_polizza}"}, {"{scadenza}"}, {"{targa}"}
              </p>
            </div>

            {/* Quick insert buttons */}
            <div className="flex flex-wrap gap-1.5">
              {data.placeholders.map(p => (
                <Button
                  key={p.key}
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 font-mono"
                  onClick={() => setEditText(prev => prev + p.key)}
                  data-testid={`insert-placeholder-${p.key}`}
                >
                  + {p.key}
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => handlePreview(editText)}
              data-testid="preview-template-btn"
            >
              <Eye className="h-4 w-4 mr-1.5" />
              Anteprima
            </Button>
            <Button onClick={handleSave} disabled={saving || !editText.trim()} data-testid="save-template-btn">
              <Save className="h-4 w-4 mr-1.5" />
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Anteprima Messaggio
            </DialogTitle>
          </DialogHeader>
          <div className="bg-[#e5ddd5] rounded-lg p-4">
            <div className="bg-[#dcf8c6] rounded-lg p-3 max-w-[85%] ml-auto shadow-sm">
              <p className="text-sm text-gray-900 whitespace-pre-wrap" data-testid="preview-text">
                {previewText}
              </p>
              <div className="flex items-center justify-end gap-1 mt-1">
                <span className="text-[10px] text-gray-500">09:30</span>
                <Check className="h-3 w-3 text-blue-500" />
                <Check className="h-3 w-3 text-blue-500 -ml-1.5" />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
